import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { AdminGuard } from '@/components/AdminGuard';
import { PageError, PageLoading, Spinner } from '@/components/Spinner';
import { api, type CellListItem, type RegisterType } from '@/lib/api';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/knowledge')({
  component: function RegistersGuard() {
    return (
      <AdminGuard>
        <RegistersPage />
      </AdminGuard>
    );
  },
});

const REGISTER_TYPES: ReadonlyArray<{ type: RegisterType; label: string; help: string }> = [
  {
    type: 'rulebook',
    label: 'Rulebook',
    help: 'CRIT-* criteria the Judge grades against. Editing here updates what the Judge reads on the next run.',
  },
  {
    type: 'conversation_design',
    label: 'Conversation design',
    help: 'Cell-specific call-flow spec — opener, identity, waterfall, consequences, close.',
  },
  {
    type: 'verbalism',
    label: 'Verbalism',
    help: 'Tone and locked-verbatim wording library for this cell.',
  },
];

function RegistersPage() {
  const q = useQuery({ queryKey: ['cells'], queryFn: api.listCells });
  const [expanded, setExpanded] = useState<string | null>(null);

  if (q.isLoading) return <PageLoading />;
  if (q.error) return <PageError message={String(q.error)} />;
  const cells = q.data?.cells ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1>Registers</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Per-cell rulebook · conversation_design · verbalism, plus the per-bank variable dictionary.
          Click a cell to expand and edit. Saves archive the previous version under{' '}
          <code className="font-mono text-xs">knowledge/_archive/</code>.
        </p>
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="w-8" />
              <th className="px-4 py-2.5 font-medium">Cell</th>
              <th className="px-4 py-2.5 font-medium">Bank</th>
              <th className="px-4 py-2.5 font-medium">Product</th>
              <th className="px-4 py-2.5 font-medium">Bucket</th>
              <th className="px-4 py-2.5 font-medium text-right">Languages</th>
              <th className="px-4 py-2.5 font-medium text-right">Personas</th>
              <th className="px-4 py-2.5 font-medium text-right">Prompts</th>
            </tr>
          </thead>
          <tbody>
            {cells.map((c) => {
              const isOpen = expanded === c.cell_slug;
              return (
                <CellRow
                  key={c.cell_slug}
                  cell={c}
                  isOpen={isOpen}
                  onToggle={() =>
                    setExpanded((p) => (p === c.cell_slug ? null : c.cell_slug))
                  }
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CellRow({
  cell,
  isOpen,
  onToggle,
}: {
  cell: CellListItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          'cursor-pointer border-t border-border transition-colors hover:bg-accent/40',
          isOpen && 'bg-accent/30',
        )}
      >
        <td className="px-3">
          {isOpen ? (
            <ChevronDown className="size-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground" />
          )}
        </td>
        <td className="px-4 py-2.5 font-mono text-xs text-foreground">{cell.cell_slug}</td>
        <td className="px-4 py-2.5 text-muted-foreground">{cell.bank}</td>
        <td className="px-4 py-2.5 text-muted-foreground">{cell.product}</td>
        <td className="px-4 py-2.5 text-muted-foreground">{cell.bucket}</td>
        <td className="px-4 py-2.5 text-right text-muted-foreground">
          {cell.languages_present.length
            ? cell.languages_present.join(' · ')
            : <span className="text-status-error-text">none</span>}
        </td>
        <td
          className={cn(
            'px-4 py-2.5 text-right tabular-nums',
            cell.persona_count === 0 ? 'text-status-error-text' : 'text-foreground',
          )}
        >
          {cell.persona_count}
        </td>
        <td
          className={cn(
            'px-4 py-2.5 text-right tabular-nums',
            cell.prompt_count === 0 ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {cell.prompt_count}
        </td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={8} className="bg-muted/10 px-6 py-5">
            <CellRegisterPanel cellSlug={cell.cell_slug} bank={cell.cell_slug.split('_')[0] as 'ENBD' | 'EI'} />
          </td>
        </tr>
      )}
    </>
  );
}

function CellRegisterPanel({
  cellSlug,
  bank,
}: {
  cellSlug: string;
  bank: 'ENBD' | 'EI';
}) {
  return (
    <div className="space-y-4">
      {REGISTER_TYPES.map(({ type, label, help }) => (
        <RegisterEditor
          key={type}
          cellSlug={cellSlug}
          type={type}
          label={label}
          help={help}
        />
      ))}
      <VarDictEditor bank={bank} />
    </div>
  );
}

function RegisterEditor({
  cellSlug,
  type,
  label,
  help,
}: {
  cellSlug: string;
  type: RegisterType;
  label: string;
  help: string;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['register', cellSlug, type],
    queryFn: () => api.getCellRegister(cellSlug, type),
    retry: 0,
  });
  const [body, setBody] = useState<string | null>(null);

  useEffect(() => {
    if (q.data && body === null) setBody(q.data.body);
  }, [q.data, body]);

  const saveMut = useMutation({
    mutationFn: () => api.putCellRegister(cellSlug, type, body ?? ''),
    onSuccess: (saved) => {
      toast.success(`${label} saved`);
      setBody(saved.body);
      qc.invalidateQueries({ queryKey: ['register', cellSlug, type] });
    },
    onError: (e) => toast.error(String(e)),
  });

  if (q.isLoading) return <RegisterShell label={label} help={help}><Spinner /></RegisterShell>;
  if (q.error)
    return (
      <RegisterShell label={label} help={help}>
        <div className="text-xs text-muted-foreground">Not uploaded yet for {cellSlug}.</div>
      </RegisterShell>
    );
  if (!q.data || body === null) return null;

  const dirty = body !== q.data.body;

  return (
    <RegisterShell
      label={label}
      help={help}
      meta={`${q.data.body_length.toLocaleString()} chars · ${q.data.line_count} lines · modified ${
        q.data.last_modified ? new Date(q.data.last_modified).toLocaleString() : '—'
      }`}
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={20}
        className="w-full rounded-md border border-input bg-card p-3 font-mono text-xs leading-relaxed shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="mt-2 flex items-center justify-end gap-3">
        <span className={cn('text-xs', dirty ? 'text-status-warning-text' : 'text-muted-foreground')}>
          {dirty ? 'unsaved changes' : 'no changes'}
        </span>
        <button
          onClick={() => setBody(q.data.body)}
          disabled={!dirty}
          className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-accent disabled:opacity-50"
        >
          Reset
        </button>
        <button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending || !body.trim() || !dirty}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
        >
          {saveMut.isPending ? (
            <Spinner className="size-3 text-primary-foreground" />
          ) : (
            <Save className="size-3.5" />
          )}
          Save
        </button>
      </div>
    </RegisterShell>
  );
}

function VarDictEditor({ bank }: { bank: 'ENBD' | 'EI' }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['vardict', bank],
    queryFn: () => api.getVardict(bank),
    retry: 0,
  });
  const [body, setBody] = useState<string | null>(null);

  useEffect(() => {
    if (q.data && body === null) setBody(q.data.body);
  }, [q.data, body]);

  const saveMut = useMutation({
    mutationFn: () => api.putVardict(bank, body ?? ''),
    onSuccess: (saved) => {
      toast.success(`${bank} variable dictionary saved`);
      setBody(saved.body);
      qc.invalidateQueries({ queryKey: ['vardict', bank] });
    },
    onError: (e) => toast.error(String(e)),
  });

  if (q.isLoading)
    return (
      <RegisterShell label={`${bank} variable dictionary`} help="">
        <Spinner />
      </RegisterShell>
    );
  if (q.error)
    return (
      <RegisterShell label={`${bank} variable dictionary`} help="">
        <div className="text-xs text-muted-foreground">Not uploaded yet for {bank}.</div>
      </RegisterShell>
    );
  if (!q.data || body === null) return null;

  const dirty = body !== q.data.body;

  return (
    <RegisterShell
      label={`${bank} variable dictionary`}
      help="Bank-level register — the canonical name + definition for each {variable} declared in this bank's prompts."
      meta={`${q.data.body_length.toLocaleString()} chars · ${q.data.line_count} lines · modified ${
        q.data.last_modified ? new Date(q.data.last_modified).toLocaleString() : '—'
      }`}
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={20}
        className="w-full rounded-md border border-input bg-card p-3 font-mono text-xs leading-relaxed shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <div className="mt-2 flex items-center justify-end gap-3">
        <span className={cn('text-xs', dirty ? 'text-status-warning-text' : 'text-muted-foreground')}>
          {dirty ? 'unsaved changes' : 'no changes'}
        </span>
        <button
          onClick={() => setBody(q.data.body)}
          disabled={!dirty}
          className="inline-flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs font-medium hover:bg-accent disabled:opacity-50"
        >
          Reset
        </button>
        <button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending || !body.trim() || !dirty}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm hover:bg-primary/90 disabled:opacity-50"
        >
          {saveMut.isPending ? (
            <Spinner className="size-3 text-primary-foreground" />
          ) : (
            <Save className="size-3.5" />
          )}
          Save
        </button>
      </div>
    </RegisterShell>
  );
}

function RegisterShell({
  label,
  help,
  meta,
  children,
}: {
  label: string;
  help: string;
  meta?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-border bg-card p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium">{label}</h3>
        {meta && <div className="text-xs text-muted-foreground">{meta}</div>}
      </div>
      {help && <p className="mt-1 text-xs text-muted-foreground">{help}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}
